// Aceleres: regras de seleção de instância para iniciar uma nova conversa.
//
// Estas funções eram privadas do ChatList.vue (Chat de Atendimento). Foram
// extraídas para que a página de Contatos use exatamente o mesmo conjunto de
// regras — quais instâncias aparecem, quais podem iniciar conversa e o motivo
// de bloqueio exibido — sem duplicar o fluxo.
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { isAceleresIndividualOwn } from 'dashboard/constants/aceleresConnectionTypes';

export const QUICK_START_CHANNEL_ICONS = {
  [INBOX_TYPES.WHATSAPP]: { icon: 'i-ri-whatsapp-fill', color: '#25D366' },
  [INBOX_TYPES.API]: { icon: 'i-ri-whatsapp-fill', color: '#25D366' },
  [INBOX_TYPES.FB]: { icon: 'i-ri-messenger-fill', color: '#0084FF' },
  [INBOX_TYPES.INSTAGRAM]: { icon: 'i-ri-instagram-fill', color: '#E1306C' },
  [INBOX_TYPES.TELEGRAM]: { icon: 'i-ri-telegram-fill', color: '#2AABEE' },
  [INBOX_TYPES.TWITTER]: { icon: 'i-ri-twitter-x-fill', color: '#111827' },
  [INBOX_TYPES.TIKTOK]: { icon: 'i-ri-tiktok-fill', color: '#111827' },
  [INBOX_TYPES.EMAIL]: { icon: 'i-ri-mail-fill', color: '#64748B' },
};

export const QUICK_START_VISIBLE_CHANNEL_TYPES = [
  INBOX_TYPES.WHATSAPP,
  INBOX_TYPES.API,
  INBOX_TYPES.FB,
  INBOX_TYPES.INSTAGRAM,
  INBOX_TYPES.TELEGRAM,
  INBOX_TYPES.TWITTER,
  INBOX_TYPES.TIKTOK,
  INBOX_TYPES.EMAIL,
];

export const QUICK_START_DEFAULT_ICON = {
  icon: 'i-ri-chat-1-fill',
  color: '#94A3B8',
};

export const QUICK_START_UNAVAILABLE_CHANNELS = [
  {
    id: 'unavailable-facebook',
    channelType: INBOX_TYPES.FB,
    name: 'Facebook',
    subtitle: 'Canal integrado ao Zoraxy',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
  {
    id: 'unavailable-instagram',
    channelType: INBOX_TYPES.INSTAGRAM,
    name: 'Instagram',
    subtitle: 'Canal integrado ao Zoraxy',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
  {
    id: 'unavailable-telegram',
    channelType: INBOX_TYPES.TELEGRAM,
    name: 'Telegram',
    subtitle: 'Canal integrado ao Zoraxy',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
  {
    id: 'unavailable-email',
    channelType: INBOX_TYPES.EMAIL,
    name: 'E-mail',
    subtitle: 'Canal integrado ao Aceleres',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
  {
    id: 'unavailable-twitter',
    channelType: INBOX_TYPES.TWITTER,
    name: 'X (Twitter)',
    subtitle: 'Canal integrado ao Zoraxy',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
  {
    id: 'unavailable-tiktok',
    channelType: INBOX_TYPES.TIKTOK,
    name: 'TikTok',
    subtitle: 'Canal integrado ao Aceleres',
    disabledReason: 'Ainda não disponível para iniciar conversas por aqui',
  },
];

export function quickStartChannelAttributes(inbox) {
  return inbox?.additional_attributes || inbox?.additionalAttributes || {};
}

export function isPersonalQuickStartInbox(inbox) {
  return isAceleresIndividualOwn(
    quickStartChannelAttributes(inbox)?.connection_type
  );
}

export function isOwnedPersonalQuickStartInbox(inbox, currentUserId) {
  if (!isPersonalQuickStartInbox(inbox)) return false;

  const attrs = quickStartChannelAttributes(inbox);
  const ownerId = Number(attrs?.owner_chatwoot_user_id || 0);
  const userId = Number(currentUserId || 0);

  return ownerId > 0 && userId > 0 && ownerId === userId;
}

export function isDeletedQuickStartInbox(inbox) {
  return inbox?.deleted === true || !!inbox?.deleted_at;
}

export function isDisconnectedQuickStartInbox(inbox) {
  const attrs = quickStartChannelAttributes(inbox);
  return !!attrs.connection_status && attrs.connection_status !== 'connected';
}

export function isEligibleQuickStartInbox(inbox, currentUserId) {
  if (![INBOX_TYPES.WHATSAPP, INBOX_TYPES.API].includes(inbox?.channel_type)) {
    return false;
  }
  if (inbox.reauthorization_required === true) return false;
  if (isDeletedQuickStartInbox(inbox)) return false;
  if (
    isPersonalQuickStartInbox(inbox) &&
    !isOwnedPersonalQuickStartInbox(inbox, currentUserId)
  ) {
    return false;
  }
  if (inbox.channel_type === INBOX_TYPES.API) {
    const attrs = quickStartChannelAttributes(inbox);
    if (!attrs.connection_type) return false;
    if (attrs.connection_status !== 'connected') return false;
  }
  return true;
}

export function eligibleInboxesForPhone(inboxes, currentUserId) {
  return (inboxes || []).filter(inbox => {
    if (![INBOX_TYPES.WHATSAPP, INBOX_TYPES.API].includes(inbox.channel_type)) {
      return false;
    }
    return isEligibleQuickStartInbox(inbox, currentUserId);
  });
}

export function cleanQuickStartChannelName(name) {
  return (name || '')
    .replace(/ · [a-f0-9]{6}$/i, '')
    .replace(/\s*[([{]?\s*vo[cç]ê\s*[)\]}]?$/i, '')
    .trim();
}

export function quickStartChannelTypeLabel(inbox) {
  if (inbox?.channel_type === INBOX_TYPES.WHATSAPP) return 'WhatsApp Oficial';
  if (inbox?.channel_type === INBOX_TYPES.API) return 'WhatsApp';
  if (inbox?.channel_type === INBOX_TYPES.FB) return 'Messenger';
  if (inbox?.channel_type === INBOX_TYPES.INSTAGRAM) return 'Instagram';
  if (inbox?.channel_type === INBOX_TYPES.TELEGRAM) return 'Telegram';
  if (inbox?.channel_type === INBOX_TYPES.TWITTER) return 'X (Twitter)';
  if (inbox?.channel_type === INBOX_TYPES.TIKTOK) return 'TikTok';
  if (inbox?.channel_type === INBOX_TYPES.EMAIL) return 'E-mail';
  return 'Canal';
}

export function quickStartChannelPrimaryLabel(inbox) {
  const attrs = quickStartChannelAttributes(inbox);
  return (
    attrs.display_phone_number ||
    attrs.phone_number ||
    attrs.whatsapp_number ||
    attrs.phone ||
    inbox?.phone_number ||
    cleanQuickStartChannelName(inbox?.name) ||
    quickStartChannelTypeLabel(inbox)
  );
}

export function quickStartChannelSubtitle(inbox, primaryLabel) {
  const attrs = quickStartChannelAttributes(inbox);
  const instanceName =
    attrs.aceleres_instance_name ||
    attrs.instance_name ||
    attrs.instance_id ||
    attrs.aceleres_instance_id ||
    cleanQuickStartChannelName(inbox?.name);

  if (instanceName && instanceName !== primaryLabel) {
    return /^inst[aâ]ncia/i.test(instanceName)
      ? instanceName
      : `Instância ${instanceName}`;
  }

  return quickStartChannelTypeLabel(inbox);
}

export function quickStartDisabledReason(inbox, isEligible, currentUserId) {
  if (
    isPersonalQuickStartInbox(inbox) &&
    !isOwnedPersonalQuickStartInbox(inbox, currentUserId)
  ) {
    return 'Instância pessoal não disponível neste fluxo';
  }

  if (
    inbox?.reauthorization_required === true ||
    isDisconnectedQuickStartInbox(inbox)
  ) {
    return 'Canal desconectado';
  }

  if (inbox?.channel_type === INBOX_TYPES.FB) {
    return 'Messenger/Meta ainda não disponível para iniciar conversas';
  }

  if (inbox?.channel_type === INBOX_TYPES.INSTAGRAM) {
    return 'Instagram/Meta ainda não disponível para iniciar conversas';
  }

  if (
    [
      INBOX_TYPES.TELEGRAM,
      INBOX_TYPES.TWITTER,
      INBOX_TYPES.TIKTOK,
      INBOX_TYPES.EMAIL,
    ].includes(inbox?.channel_type)
  ) {
    return 'Ainda não disponível para iniciar conversas por aqui';
  }

  if (!isEligible) {
    return 'Canal indisponível para este contato';
  }

  return '';
}

export function quickStartChannelSortScore(channel) {
  const order = {
    [INBOX_TYPES.WHATSAPP]: 0,
    [INBOX_TYPES.API]: 1,
    [INBOX_TYPES.FB]: 2,
    [INBOX_TYPES.INSTAGRAM]: 3,
    [INBOX_TYPES.TELEGRAM]: 4,
    [INBOX_TYPES.TWITTER]: 5,
    [INBOX_TYPES.TIKTOK]: 6,
    [INBOX_TYPES.EMAIL]: 7,
  };
  return order[channel.channelType] ?? 9;
}

// Monta a lista exibida no modal "Selecione um canal": instâncias visíveis,
// marcadas com canStart/disabledReason, mais os canais ainda indisponíveis.
export function buildQuickStartChannels({
  inboxes,
  extraInboxes,
  eligibleInboxIds,
  currentUserId,
}) {
  const eligibleIds = new Set((eligibleInboxIds || []).map(id => Number(id)));
  const byId = new Map();

  (inboxes || [])
    .filter(
      inbox =>
        QUICK_START_VISIBLE_CHANNEL_TYPES.includes(inbox?.channel_type) &&
        (!isPersonalQuickStartInbox(inbox) ||
          isOwnedPersonalQuickStartInbox(inbox, currentUserId))
    )
    .forEach(inbox => byId.set(Number(inbox.id), inbox));

  (extraInboxes || []).forEach(inbox => byId.set(Number(inbox.id), inbox));

  const channels = Array.from(byId.values())
    .map(inbox => {
      const id = Number(inbox.id);
      const isEligible = eligibleIds.has(id);
      const canStart =
        [INBOX_TYPES.WHATSAPP, INBOX_TYPES.API].includes(inbox.channel_type) &&
        isEligible;
      const primaryLabel = quickStartChannelPrimaryLabel(inbox);

      return {
        id,
        channelType: inbox.channel_type,
        name: primaryLabel,
        subtitle: quickStartChannelSubtitle(inbox, primaryLabel),
        instanceId: Number(
          quickStartChannelAttributes(inbox).aceleres_instance_id ||
            quickStartChannelAttributes(inbox).instance_id ||
            0
        ),
        icon:
          QUICK_START_CHANNEL_ICONS[inbox.channel_type] ||
          QUICK_START_DEFAULT_ICON,
        canStart,
        disabledReason: quickStartDisabledReason(
          inbox,
          isEligible,
          currentUserId
        ),
      };
    })
    .filter(channel => channel.name);

  const availableChannelTypes = new Set(
    channels.map(channel => channel.channelType)
  );
  QUICK_START_UNAVAILABLE_CHANNELS.forEach(channel => {
    if (availableChannelTypes.has(channel.channelType)) return;
    channels.push({
      ...channel,
      icon:
        QUICK_START_CHANNEL_ICONS[channel.channelType] ||
        QUICK_START_DEFAULT_ICON,
      canStart: false,
    });
  });

  const nameCounts = channels.reduce((counts, channel) => {
    const key = (channel.name || '').trim().toLocaleLowerCase();
    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());

  return channels
    .map(channel => {
      const key = (channel.name || '').trim().toLocaleLowerCase();
      if ((nameCounts.get(key) || 0) < 2) return channel;

      const identity =
        channel.instanceId > 0
          ? `instância #${channel.instanceId}`
          : `inbox #${channel.id}`;

      return {
        ...channel,
        name: `${channel.name} · ${identity}`,
      };
    })
    .sort((a, b) => {
      if (a.canStart !== b.canStart) return a.canStart ? -1 : 1;
      const typeDiff =
        quickStartChannelSortScore(a) - quickStartChannelSortScore(b);
      if (typeDiff !== 0) return typeDiff;
      return (a.name || '').localeCompare(b.name || '', 'pt-BR', {
        sensitivity: 'base',
      });
    });
}
